// src/components/CompareDrawer.js
import React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Image,
  Text,
  VStack,
  HStack,
  IconButton,
  Badge,
  Icon,
  Wrap,
  Box,
} from '@chakra-ui/react';
import { 
  X,
  Package,
  Tag,
  Users,
  Star,
  ShoppingCart,
  Palette,
  List,
  CircleDollarSign,
  Scale,
  Medal,
} from 'lucide-react';

// MatchResults.jsから定数をインポート
import { 
  colorOptions, 
  featureOptions, 
  colorSchemeMap 
} from './MatchResults';

// 価格帯の表示用
const getPriceRangeLabel = (priceRange) => {
  switch(priceRange) {
    case '60k':
      return '6万台のランドセル';
    case '70k':
      return '7万台のランドセル';
    case 'all':
      return '指定なし';
    default:
      return '不明';
  }
};

const CompareDrawer = ({ isOpen, onClose, compareItems, removeFromCompare }) => {
  // 比較項目の定義
  const compareFeatures = [
    { 
      key: 'code', 
      label: '品番',
      icon: Package,
      render: (val) => val
    },
    { 
      key: 'name', 
      label: '商品名',
      icon: Package,
      render: (val) => val
    },
    { 
      key: 'price', 
      label: '価格',
      icon: Tag,
      render: (val, item) => (
        <HStack spacing={2}>
          <Text>{val ? `${val.toLocaleString()}円` : '情報なし'}</Text>
          {item.priceRange && (
            <Badge colorScheme="green">
              {getPriceRangeLabel(item.priceRange)}
            </Badge>
          )}
        </HStack>
      )
    },
    { 
      key: 'gender', 
      label: '性別',
      icon: Users,
      render: (val) => 
        val === 'boy' ? '男の子' : 
        val === 'girl' ? '女の子' : 
        '男女共用'
    },
    { 
      key: 'colors', 
      label: 'カラー',
      icon: Palette,
      render: (colors) => (
        <Wrap spacing={2}>
          {colors?.map(color => (
            <Badge 
              key={color} 
              colorScheme={colorSchemeMap[color]}
              variant="subtle"
            >
              {colorOptions.find(opt => opt.value === color)?.label}
            </Badge>
          ))}
        </Wrap>
      )
    },
    { 
      key: 'features', 
      label: '特徴',
      icon: List,
      render: (features) => (
        <Wrap spacing={2}>
          {features?.map(feature => (
            <Badge 
              key={feature} 
              colorScheme="green"
              variant="subtle"
            >
              {featureOptions.find(opt => opt.value === feature)?.label}
            </Badge>
          ))}
        </Wrap>
      )
    },
    { 
      key: 'matchScore', 
      label: 'マッチ度',
      icon: Star,
      render: (val) => (
        <Badge colorScheme="blue" fontSize="md">
          {val}%
        </Badge>
      )
    }
  ];

  return (
    <Drawer 
      isOpen={isOpen} 
      placement="bottom" 
      onClose={onClose} 
      size={{ base: "full", md: "xl" }}
    >
      <DrawerOverlay />
      <DrawerContent 
        borderTopRadius={{ base: 0, md: "lg" }}
      >
        <DrawerCloseButton 
          size={{ base: "lg", md: "md" }}
          top={{ base: 4, md: 2 }}
          right={{ base: 4, md: 2 }}
        />
        <DrawerHeader 
          borderBottomWidth="1px"
          bg="blue.50"
          py={{ base: 6, md: 4 }}
        >
          <HStack spacing={2}>
            <Icon as={Star} color="blue.500" />
            <Text fontSize={{ base: "lg", md: "md" }}>ランドセル比較</Text>
          </HStack>
        </DrawerHeader>
        <DrawerBody 
          p={{ base: 3, md: 6 }}
          overflowX="auto"
        >
          <Table variant="simple" size={{ base: "sm", md: "md" }}>
            <Thead>
              <Tr>
                <Th 
                  width={{ base: "120px", md: "150px" }}
                  bg="gray.50"
                  position="sticky"
                  left={0}
                  zIndex={1}
                  fontSize={{ base: "xs", md: "sm" }}
                >
                  <HStack spacing={2}>
                    <Icon as={List} color="gray.500" />
                    <Text>項目</Text>
                  </HStack>
                </Th>
                {compareItems.map((item, index) => (
                  <Th 
                    key={index}
                    minW={{ base: "250px", md: "300px" }}
                    px={{ base: 2, md: 6 }}
                  >
                    <VStack align="stretch" spacing={4}>
                      <HStack justify="space-between">
                        <Text 
                          fontSize={{ base: "xs", md: "sm" }} 
                          fontWeight="bold"
                        >
                          {index + 1}番目の商品
                        </Text>
                        <IconButton
                          icon={<X size={16} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="gray"
                          onClick={() => removeFromCompare(item)}
                          aria-label="比較から削除"
                        />
                      </HStack>
                      <Box 
                        p={2} 
                        borderWidth="1px" 
                        borderRadius="md"
                        bg="white"
                      >
                        {item.imageUrl && (
                          <Image
                            src={item.imageUrl}
                            alt={item.name}
                            maxH={{ base: "120px", md: "150px" }}
                            objectFit="contain"
                            mx="auto"
                            borderRadius="md"
                          />
                        )}
                      </Box>
                    </VStack>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {compareFeatures.map((feature) => (
                <Tr key={feature.key}>
                  <Td 
                    bg="gray.50"
                    position="sticky"
                    left={0}
                    zIndex={1}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    <HStack spacing={2}>
                      <Icon as={feature.icon} color="gray.500" size={16} />
                      <Text fontWeight="bold">{feature.label}</Text>
                    </HStack>
                  </Td>
                  {compareItems.map((item, index) => (
                    <Td 
                      key={index}
                      fontSize={{ base: "xs", md: "sm" }}
                      px={{ base: 2, md: 6 }}
                    >
                      {feature.render(item[feature.key], item)}
                    </Td>
                  ))}
                </Tr>
              ))}
              <Tr>
                <Td 
                  bg="gray.50"
                  position="sticky"
                  left={0}
                  zIndex={1}
                  fontSize={{ base: "xs", md: "sm" }}
                >
                  <HStack spacing={2}>
                    <Icon as={ShoppingCart} color="gray.500" size={16} />
                    <Text fontWeight="bold">購入</Text>
                  </HStack>
                </Td>
                {compareItems.map((item, index) => (
                  <Td 
                    key={index}
                    px={{ base: 2, md: 6 }}
                  >
                    {item.purchaseUrl && (
                      <Button
                        as="a"
                        href={item.purchaseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        colorScheme="pink"
                        size={{ base: "xs", md: "sm" }}
                        leftIcon={<ShoppingCart size={14} />}
                        width={{ base: "full", md: "auto" }}
                      >
                        購入ページへ
                      </Button>
                    )}
                  </Td>
                ))}
              </Tr>
            </Tbody>
          </Table>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default CompareDrawer;